import { getCookie, setCookie } from './cookies.util';

// The backend rejects requests whose `createdAt` header is more than ~20s
// behind its own clock, so a device with a slightly slow clock gets a 401 on
// every call. We keep an offset between the device clock and the server clock
// and use it when stamping requests.

// Below this, a 401 isn't caused by clock drift (e.g. an expired token).
const MIN_CORRECTION_MS = 5000;

// A device's clock error is stable, so the offset is kept in a cookie for a
// day and only the first visit waits for a measurement. If the user fixes
// their clock in the meantime, the next 401 re-measures it.
const OFFSET_COOKIE = 'serverClockOffset';
const OFFSET_COOKIE_DAYS = 1;

let clockOffset = 0;
let pendingSync: Promise<boolean> | null = null;

const readStoredOffset = (): number | null => {
  const stored = getCookie(OFFSET_COOKIE);
  const offset = stored ? Number(stored) : NaN;
  return Number.isFinite(offset) ? offset : null;
};

const storeOffset = (offset: number) =>
  setCookie(OFFSET_COOKIE, String(Math.round(offset)), OFFSET_COOKIE_DAYS);

export const getServerNow = () => Date.now() + clockOffset;

// Upper bound on how long a sync may hold up requests on a slow connection.
const SYNC_TIMEOUT_MS = 8000;

const measureOffset = async (): Promise<number> => {
  // AbortController + setTimeout rather than AbortSignal.timeout(), which
  // older Safari versions don't support.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);

  try {
    const sentAt = Date.now();
    const res = await fetch('/api/server-time', {
      cache: 'no-store',
      signal: controller.signal,
    });
    const { now } = await res.json();

    // The server stamped `now` some time after `sentAt`, so this errs towards
    // stamping requests slightly ahead - which the backend accepts - rather
    // than behind, which it rejects.
    return Number(now) - sentAt;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Re-measures the device/server clock offset. Resolves `true` when the offset
 * changed enough that a request which failed with 401 is worth retrying.
 * Concurrent callers share one measurement.
 */
export const syncServerClock = (): Promise<boolean> => {
  if (typeof window === 'undefined') return Promise.resolve(false);

  if (!pendingSync) {
    pendingSync = measureOffset()
      .then((offset) => {
        if (!Number.isFinite(offset)) return false;
        const changed = Math.abs(offset - clockOffset) >= MIN_CORRECTION_MS;
        if (changed) clockOffset = offset;
        storeOffset(clockOffset);
        return changed;
      })
      .catch((error) => {
        console.error('Server clock sync failed', error);
        return false;
      })
      .finally(() => {
        pendingSync = null;
      });
  }

  return pendingSync;
};

let initialSync: Promise<unknown> | null = null;

/**
 * Makes sure the offset is known before the first API request goes out, so a
 * device with a wrong clock never sends a request that gets a 401. Uses the
 * remembered offset when there is one; otherwise waits for a measurement.
 * A failed or timed-out sync falls back to the device clock (and the 401
 * retry in the axios instance).
 */
export const ensureClockSynced = () => {
  if (typeof window === 'undefined') return Promise.resolve();

  if (!initialSync) {
    const storedOffset = readStoredOffset();
    if (storedOffset !== null) {
      clockOffset = storedOffset;
      initialSync = Promise.resolve();
    } else {
      initialSync = syncServerClock();
    }
  }

  return initialSync;
};
